import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Car, ClipboardCheck, CreditCard, FileText, Gauge, CalendarDays, KeyRound, LogOut, MessageSquare, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/constants";

const NAV = [
  ["dashboard", "Dashboard", Gauge, "/portal"], ["vehicles", "My Garage", Car, "/portal/vehicles"],
  ["quotes", "Quotes", FileText, "/portal/quotes"], ["bookings", "Bookings", CalendarDays, "/portal/bookings"],
  ["tracking", "Live Jobs", Wrench, "/portal/jobs"], ["approvals", "Approvals", ClipboardCheck, "/portal/approvals"],
  ["billing", "Billing", CreditCard, "/portal/billing"], ["messages", "Messages", MessageSquare, "/portal/messages"],
  ["password", "Password", KeyRound, "/portal/password"],
];

export const ConsoleShell = ({ nav, title, testPrefix, account, onLogout }) => {
  const auth = useAuth();
  const user = account || auth.user;
  const go = useNavigate();
  const out = async () => { await (onLogout || auth.logout)(); go(onLogout ? "/insurer/login" : "/"); };
  const link = ({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#F86900] text-[#0D141A]" : "text-white/75 hover:bg-white/10 hover:text-white"}`;
  return (
    <div className="min-h-screen bg-[#F4F4F4] lg:flex">
      <aside className="hidden lg:flex w-[260px] shrink-0 flex-col bg-[#020165] text-white p-5 sticky top-0 h-screen" data-testid={`${testPrefix}-sidebar-nav`}>
        <Link to="/" className="flex items-center gap-2.5 mb-8"><img src={LOGO} alt="" className="h-9 w-9 rounded bg-white p-0.5" /><span className="font-display font-extrabold">Accelary<span className="text-[#F86900]"> {title}</span></span></Link>
        <nav className="space-y-1 flex-1">
          {nav.map(([k, l, Icon, to]) => <NavLink key={k} to={to} end={!to.endsWith("/insurance") && !to.endsWith("/requests")} data-testid={`${testPrefix}-nav-${k}`} className={link}><Icon className="h-4 w-4" />{l}</NavLink>)}
        </nav>
        <div className="border-t border-white/10 pt-4 text-sm">
          <p className="font-semibold truncate" data-testid={`${testPrefix}-user-name`}>{user?.name || user?.email}</p>
          <p className="text-white/50 text-xs truncate">{user?.email}</p>
          <button onClick={out} data-testid={`${testPrefix}-logout-button`} className="mt-3 flex items-center gap-2 text-white/70 hover:text-[#F86900]"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 bg-[#020165] text-white flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex items-center gap-2"><img src={LOGO} alt="" className="h-8 w-8 rounded bg-white p-0.5" /><span className="font-display font-bold">{title}</span></Link>
        <button onClick={out} data-testid={`${testPrefix}-mobile-logout`} aria-label="Sign out"><LogOut className="h-5 w-5" /></button>
      </header>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 pb-24 lg:pb-10"><Outlet /></main>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t flex overflow-x-auto" data-testid={`${testPrefix}-mobile-nav`}>
        {nav.map(([k, l, Icon, to]) => (
          <NavLink key={k} to={to} end data-testid={`${testPrefix}-mobile-nav-${k}`} className={({ isActive }) => `min-w-[76px] flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium whitespace-nowrap ${isActive ? "text-[#F86900]" : "text-[#56585E]"}`}>
            <Icon className="h-5 w-5" />{l}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export const PortalHeading = ({ title, sub, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
    <div><h1 className="font-display font-extrabold text-3xl tracking-tight text-[#0D141A]" data-testid="portal-page-title">{title}</h1>{sub && <p className="text-sm text-[#56585E] mt-1">{sub}</p>}</div>
    {action}
  </div>
);

export const Empty = ({ children, testId }) => <div className="rounded-2xl border border-dashed border-[#56585E]/30 p-10 text-center text-sm text-[#56585E]" data-testid={testId}>{children}</div>;

export default function PortalLayout() {
  return <ConsoleShell nav={NAV} title="Portal" testPrefix="portal" />;
}
